import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { toast } from 'components/ui/use-toast';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import * as Grading from 'modules/grading';
import type { GradedExportRow } from 'modules/grading/api';

// Column order + English fallback labels for the CSV header row.
const CSV_COLUMNS: { key: keyof GradedExportRow; label: string }[] = [
  { key: 'candidateName', label: 'Candidate' },
  { key: 'candidateEmail', label: 'Email' },
  { key: 'test', label: 'Test' },
  { key: 'question', label: 'Question' },
  { key: 'questionType', label: 'Type' },
  { key: 'score', label: 'Score' },
  { key: 'maxPoints', label: 'Max' },
  { key: 'grader', label: 'Grader' },
  { key: 'gradedAt', label: 'Graded at' },
  { key: 'note', label: 'Note' },
];

const escapeCsv = (value: unknown): string => {
  let s = value == null ? '' : String(value);
  // Neutralize spreadsheet formula/DDE injection: candidate-controlled fields
  // (e.g. a guest's typed name) starting with = + - @ execute when the file is
  // opened in Excel/Sheets. Prefix with a single quote to force a text cell.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows: GradedExportRow[]): string => {
  const head = CSV_COLUMNS.map((c) => c.label).join(',');
  const body = rows.map((r) => CSV_COLUMNS.map((c) => escapeCsv(r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
};

/** UX-only gating — the server enforces the same permission and org scope. */
export const GradingExportButton: React.FC<{ orgId?: string }> = ({ orgId }) => {
  const { t } = useTranslation();
  const canExport = usePermission(PERMISSIONS.GRADING_EXPORT);
  const [loading, setLoading] = useState(false);

  if (!canExport || !orgId) return null;

  const handleExport = async () => {
    setLoading(true);
    try {
      const { data } = await Grading.Api.GetGradedExport();
      if (!data || data.length === 0) {
        toast.info(t('grading_export_empty'));
        return;
      }
      const blob = new Blob([toCsv(data)], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'grades.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('grading_export_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={handleExport} disabled={loading}>
      {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
      {t('export')}
    </Button>
  );
};

export default GradingExportButton;
